import React, { useEffect, useState } from 'react'
import api from 'config/axiosConfig'
import { useParams, useLocation, useSearchParams, useNavigate } from 'react-router-dom'
import IssueCard from '../IssueCard'
import IssueProfileTabs from './IssueProfileTabs'
import { setDocumentTitle } from 'util/pageTitles'
import LoadingSkeleton from 'ui/LoadingSkeleton'
import NotFoundPage from 'components/ResponsePages/404NotFoundPage'
import { AxiosError } from 'axios'
import ServerErrorPage from 'components/ResponsePages/500ServerErrorPage'

const IssueProfile = () => {
    const { cname } = useParams<{ cname: string }>()
    const location = useLocation()
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const [issue, setIssue] = useState(null)
    const [error, setError] = useState('')

    useEffect(() => {
        setDocumentTitle(`Issue - ${cname?.replace(/_/g, ' ')}`)
    }, [cname])

    useEffect(() => {
        const fetchIssue = async () => {
            try {
                // First try to get the issue by name
                try {
                    const response = await api.get(`/api/issues/name/${cname}`)
                    const issueData = response.data
                    const params = new URLSearchParams(location.search)
                    if (params.get('upvoted') === 'true') {
                        issueData.user_vote = 'user_upvote'
                    }
                    setIssue(issueData)
                    return
                } catch (nameError) {
                    // If name lookup fails, try id lookup
                    if (!isNaN(Number(cname))) {
                        try {
                            const idResponse = await api.get(`/api/issues/id/${cname}`)
                            const issueData = idResponse.data
                            
                            // Redirect to the canonical URL using cname
                            navigate(`/issue/${issueData.cname}${location.search}`, { replace: true })
                            return
                        } catch (idError) {
                            // If both lookups fail, throw error to be caught by outer catch
                            throw idError
                        }
                    } else {
                        // If cname isn't a number, throw the original error
                        throw nameError
                    }
                }
            } catch (err) {
                if (err instanceof AxiosError && err.response?.status === 404) {
                    setError('404: Failed to load issue profile. Please try again later.')
                } else {
                    setError('Failed to load issue profile. Please try again later.')
                }
            }
        }

        fetchIssue()
    }, [cname, location.search, searchParams, navigate])

    if (error) {
        return error.match(/404/) ? <NotFoundPage displayStyle={`page`} errorMessage={`Sorry, we couldn't find this issue.`} buttonText={`View Other Issues`} buttonHref={`/issues/new`} /> : <ServerErrorPage />
    }

    if (!issue) {
        return (
            <LoadingSkeleton count={1} height="h-80" className="mt-4" />
        )
    }

    return (
        <div className="py-0 relative">
            <div>
                <IssueCard
                    issue={issue}
                    showMeta={true}
                    truncateDescription={true}
                    isIssueProfilePage={true}
                />
            </div>
            <IssueProfileTabs issue={issue} />
        </div>
    )
}

export default IssueProfile
